import config from "src/config";
import { ResponseDto } from 'src/dtos/common/responseDto';
import { getData, postData, patchData, deleteData, deleteDataList } from 'src/utils/axios/serverHelper';
import { MST4030_R01HeaderDto, MST4030_R01DetailDto } from './Dto_MST4030_R01';
import { MST4030_R01Filter } from "./Store_MST4030_R01";

const baseUrl = `${config.apiUrl}/MST4030_R01`;

/**
 * 헤더 목록 조회 (TOOLING 설비 목록)
 * @param option 'search' (전체 조회) 또는 '' (제한된 조회, 기본값 10000건)
 * @param params 조회 조건
 * @param listLength 목록 길이
 * @param refetchListLength 재조회 목록 길이
 */
export const getMST4030_R01HeaderList = async (
	option: string, 
	params: Partial<MST4030_R01Filter>, 
	listLength: number = 0,
	refetchListLength: number = 0
) => {
	const url = option === 'search' 
		? `${baseUrl}/header/search`
		: `${baseUrl}/header`;

	return await getData<ResponseDto<MST4030_R01HeaderDto[]>>(url, {
		params: {
			...params,
			listLength,
			refetchListLength
		}
	});
}

/**
 * 헤더 전체 개수 조회
 * @param params 조회 조건
 */
export const getMST4030_R01HeaderAllCount = async (params: Partial<MST4030_R01Filter>) => {
	const url = `${baseUrl}/header/allCount`;

	return await getData<ResponseDto<number>>(url, {
		params: {
			...params
		}
	});
}

/**
 * 디테일 목록 조회 (점검 이력 목록)
 * @param option 'search' (전체 조회) 또는 '' (제한된 조회)
 * @param params 조회 조건
 * @param listLength 목록 길이
 * @param refetchListLength 재조회 목록 길이
 */
export const getMST4030_R01DetailList = async (
	option: string,
	params: Partial<MST4030_R01Filter>,
	listLength: number = 0,
	refetchListLength: number = 0
) => {
	const url = option === 'search'
		? `${baseUrl}/detail/search`
		: `${baseUrl}/detail`;

	return await getData<ResponseDto<MST4030_R01DetailDto[]>>(url, {
		params: {
			...params,
			listLength,
			refetchListLength
		}
	});
}

/**
 * 디테일 전체 개수 조회
 * @param params 조회 조건
 */
export const getMST4030_R01DetailAllCount = async (params: Partial<MST4030_R01Filter>) => {
	const url = `${baseUrl}/detail/allCount`;

	return await getData<ResponseDto<number>>(url, {
		params: {
			...params
		}
	});
}

/**
 * 헤더 등록 (TOOLING 설비 등록)
 * @param data 헤더 DTO
 */
export const createMST4030_R01Header = async (data: MST4030_R01HeaderDto) => {
	const url = `${baseUrl}/header`;
	return await postData<MST4030_R01HeaderDto, ResponseDto<MST4030_R01HeaderDto>>(url, data);
}

/**
 * 헤더 수정 (TOOLING 설비 수정)
 * @param data 헤더 DTO
 */
export const patchMST4030_R01Header = async (data: MST4030_R01HeaderDto) => {
	const url = `${baseUrl}/header`;
	return await patchData<MST4030_R01HeaderDto, ResponseDto<MST4030_R01HeaderDto>>(url, data);
}

/**
 * 헤더 삭제 (TOOLING 설비 삭제)
 * @param equipmentCd 설비코드
 */
export const deleteMST4030_R01Header = async (equipmentCd: string) => {
	const url = `${baseUrl}/header/${equipmentCd}`;
	return await deleteData<ResponseDto<any>>(url);
}

/**
 * 디테일 등록 (점검 이력 등록)
 * @param data 디테일 DTO (inspectDt는 string 형식으로 전송)
 */
export const createMST4030_R01Detail = async (data: MST4030_R01DetailDto) => {
	const url = `${baseUrl}/detail`;
	return await postData<MST4030_R01DetailDto, ResponseDto<MST4030_R01DetailDto>>(url, data);
}

/**
 * 디테일 수정 (점검 이력 수정)
 * @param data 디테일 DTO (inspectDt는 string 형식으로 전송)
 */
export const patchMST4030_R01Detail = async (data: MST4030_R01DetailDto) => {
	const url = `${baseUrl}/detail`;
	return await patchData<MST4030_R01DetailDto, ResponseDto<MST4030_R01DetailDto>>(url, data);
}

/**
 * 디테일 삭제 (점검 이력 삭제)
 * @param data 디테일 DTO (복합키 필드 포함)
 */
export const deleteMST4030_R01Detail = async (data: {
	inspectDt: string;
	eqmCd: string;
	inspectCycle: string;
	inspectType: string;
}) => {
	const url = `${baseUrl}/detail`;
	return await deleteDataList<typeof data, ResponseDto<any>>(url, data);
}

/**
 * 특정 설비의 엑셀 다운로드 (헤더 + 디테일)
 * @param equipmentCd 설비코드
 */
export const downloadExcel_MST4030_R01 = async (equipmentCd: string) => {
	const url = `${baseUrl}/header/${equipmentCd}/excel`;

	return await getData<ResponseDto<string>>(url);
}
